<script setup lang="ts">
import { z } from 'zod'
import { phoneSchema } from '~/schemas'

const auth = useAuthStore()

const schema = toTypedSchema(
  z
    .object({
      first_name: z.string().trim().min(1),
      last_name: z.string().trim().min(1),
      email: z.string().email(),
      phone: phoneSchema,
      password: z.string().min(12),
      password_confirmation: z.string().min(12),
    })
    .refine((data) => data.password === data.password_confirmation, {
      message: 'Passwords do not match',
      path: ['password_confirmation'],
    }),
)

const { errors, defineField, handleSubmit, isSubmitting } = useForm({
  validationSchema: schema,
})

const [firstName, firstNameAttrs] = defineField('first_name')
const [lastName, lastNameAttrs] = defineField('last_name')
const [email, emailAttrs] = defineField('email')
const [phone, phoneAttrs] = defineField('phone')
const [password, passwordAttrs] = defineField('password')
const [passwordConfirmation, passwordConfirmationAttrs] = defineField(
  'password_confirmation',
)

const onSubmit = handleSubmit(async (values) => {
  await auth.register(values)
})
</script>

<template>
  <form @submit="onSubmit">
    <div class="row gx-xs">
      <div class="col-md-6">
        <AppTextField
          v-model="firstName"
          v-bind="firstNameAttrs"
          :error="!!errors.first_name"
          class="mb-3 md:mb-4"
          :placeholder="$t('string.First-name')"
          full-width
        />
      </div>

      <div class="col-md-6">
        <AppTextField
          v-model="lastName"
          v-bind="lastNameAttrs"
          :error="!!errors.last_name"
          class="mb-3 md:mb-4"
          :placeholder="$t('string.Last-name')"
          full-width
        />
      </div>
    </div>

    <AppPhoneField
      v-model="phone"
      v-bind="phoneAttrs"
      :error="!!errors.phone"
      class="mb-3 md:mb-4"
      type="tel"
      :placeholder="$t('string.Phone-number')"
      full-width
    />

    <AppTextField
      v-model="email"
      v-bind="emailAttrs"
      :error="!!errors.email"
      class="mb-3 md:mb-4"
      type="email"
      :placeholder="$t('string.Email-address')"
      full-width
    />

    <AppTextField
      v-model="password"
      v-bind="passwordAttrs"
      :error="!!errors.password"
      class="mb-3 md:mb-4"
      type="password"
      :placeholder="$t('string.Password')"
      full-width
    />

    <AppTextField
      v-model="passwordConfirmation"
      v-bind="passwordConfirmationAttrs"
      :error="!!errors.password_confirmation"
      class="mb-5 md:mb-8"
      type="password"
      :placeholder="$t('string.Confirm-password')"
      full-width
    />

    <AppButton type="submit" full-width :loading="isSubmitting">
      {{ $t('string.Register') }}
    </AppButton>
  </form>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';
</style>
